import { TGlobalStores } from '.';
import { EColumnType, IColumnData } from '../data/model/model';
import { Curve2dUtils } from '../math/Curve2dUtils';
import { IPoint2d } from '../math/IPoint2d';
import { getRotatedRectangleVertices, isCircleInPolygon } from '../math/utils';
import { IWallEdge } from '../types';
import { isCylinderColumnIntersectPolygon, isPolygonInOrOnPolygon, isPolygonIntersectPolygon } from '../utils';
import { getPathFromEdges } from '../utils/edge';
import { dispersePath } from '../utils/shape';
import { BaseListStore } from './base/baseListStore';

export type TColumnDatasInRangeMode = 'intersect' | 'center';

export class StructStore extends BaseListStore<IColumnData> {
  constructor(gs: TGlobalStores) {
    super(gs);
  }

  getColumnData(columnId: string) {
    return this.get(columnId);
  }

  getColumnDatas() {
    return this.getList();
  }

  addColumnData(columnData: IColumnData) {
    this.add(columnData);
  }

  removeColumnData(id: string) {
    this.remove(id);
  }

  updateColumnData(id: string, columnData: Partial<IColumnData>) {
    this.frequentlyUpdate(id, columnData);
  }

  getColumnDatasInPathInfos(
    pathInfos: IWallEdge[],
    holeInfos: IWallEdge[][] = [],
    mode: TColumnDatasInRangeMode = 'intersect'
  ) {
    const polygon = dispersePath(getPathFromEdges(pathInfos));
    const holes = holeInfos.map((hole) => dispersePath(getPathFromEdges(hole)));

    return this.getColumnDatasInPolygon(polygon, holes, mode);
  }

  getColumnDatasInPolygon(polygon: IPoint2d[], holes: IPoint2d[][] = [], mode: TColumnDatasInRangeMode = 'intersect') {
    if (polygon.length < 3) return [];

    const validHoles = holes.filter((hole) => hole.length >= 3);

    return this.getList().filter((column) => {
      const { type, center, rotation } = column;
      const isCylinder = type === EColumnType.cylinder;
      const columnPolygon: IPoint2d[] = isCylinder
        ? [
            { x: center.x - column.radius, y: center.y + column.radius },
            { x: center.x + column.radius, y: center.y + column.radius },
            { x: center.x + column.radius, y: center.y - column.radius },
            { x: center.x - column.radius, y: center.y - column.radius }
          ]
        : getRotatedRectangleVertices(column.width, column.thickness, center, rotation);

      for (const hole of validHoles) {
        switch (mode) {
          case 'center':
            if (Curve2dUtils.isPointInPolygon(center, hole)) {
              return false;
            }
            break;
          case 'intersect':
            if (isCylinder && isCircleInPolygon(center, column.radius, hole)) return false;
            if (!isCylinder && isPolygonInOrOnPolygon(columnPolygon, hole)) return false;
            break;
          default:
            break;
        }
      }

      switch (mode) {
        case 'center':
          if (Curve2dUtils.isPointInPolygon(center, polygon)) {
            return true;
          }
          break;
        case 'intersect':
          if (isCylinder && isCylinderColumnIntersectPolygon(center, column.radius, polygon, validHoles) !== null)
            return true;
          if (!isCylinder && isPolygonIntersectPolygon(columnPolygon, polygon)) {
            return true;
          }
          break;
        default:
          return false;
      }
      return false;
    });
  }

  clearAll() {
    this.clear();
  }

  coverDatas(columnDatas: IColumnData[]) {
    this.clear();

    try {
      for (const columnData of columnDatas) {
        this.add(columnData);
      }
    } catch (error) {
      console.error('StructStore coverDatas error', error);
      this.clearAll();
    }
  }
}
